import { useEffect, useState, useCallback } from "react";
import api, { billingErrorMessage } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AISearchVisibilityPage() {
  const { activeProjectId } = useProjects();
  const [tests, setTests] = useState([]);
  const [prompt, setPrompt] = useState("What's the best alternative in this category?");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/ai-visibility/${activeProjectId}`);
    setTests(data);
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setLoading(true);
    try {
      await api.post("/ai-visibility/run", { project_id: activeProjectId, prompt });
      toast.success("Visibility test complete");
      load();
    } catch (e) {
      toast.error(billingErrorMessage(e) || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const avg = tests.length ? Math.round(tests.reduce((a, t) => a + t.score, 0) / tests.length) : 0;

  return (
    <DashboardLayout>
      <PageHeader
        overline="AI Search Visibility"
        title="Be the answer when AI assistants get asked."
        description="Simulate buying-intent prompts and get exact pages, schema, and entities to publish."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-border rounded-sm p-5">
          <p className="editorial-overline">Avg score</p>
          <p className="font-serif text-4xl mt-2">{avg} / 100</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <p className="editorial-overline">Prompts tested</p>
          <p className="font-serif text-4xl mt-2">{tests.length}</p>
        </div>
        <div className="bg-navy-900 text-ivory-50 rounded-sm p-5 border border-navy-900">
          <p className="editorial-overline !text-gold-400">Run new test</p>
          <div className="mt-3 flex gap-2">
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              data-testid="visibility-prompt-input"
              className="bg-white/10 border-ivory-200/15 text-ivory-50 rounded-sm placeholder:text-ivory-200/40"
            />
            <button onClick={run} disabled={loading} data-testid="visibility-run-btn" className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50">
              {loading ? "…" : "Run"}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {tests.map((t) => (
          <div key={t.id} className="bg-white border border-border rounded-sm p-6" data-testid={`visibility-test-${t.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="editorial-overline">Prompt</p>
                <p className="font-serif text-xl mt-1">&ldquo;{t.prompt}&rdquo;</p>
              </div>
              <div className="text-right shrink-0">
                <p className="editorial-overline">Score</p>
                <p className="font-serif text-3xl text-gold-600">{t.score}</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3 italic">{t.positioning}</p>

            <div className="grid md:grid-cols-2 gap-5 mt-5">
              <Group title="Missing content" items={t.missing_content} />
              <Group title="Pages to create" items={t.pages_to_create} />
              <Group title="Entities to strengthen" items={t.entities_to_strengthen} />
              <Group title="Schema recommendations" items={t.schema_recommendations} />
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function Group({ title, items }) {
  return (
    <div>
      <p className="editorial-overline">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {(items || []).map((x) => <li key={x} className="text-sm text-navy-900/85">• {x}</li>)}
      </ul>
    </div>
  );
}
